import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Mail, User, FileText, Clock, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { RealtimeSubmission } from '@/hooks/useRealtimeSubmissions';

interface ActivityFeedProps {
  submissions: RealtimeSubmission[];
  isLoading?: boolean;
  onItemClick?: (submission: RealtimeSubmission) => void;
}

// Extraer nombre de las respuestas
function extractName(rawAnswers: Record<string, unknown> | null): string {
  if (!rawAnswers) return 'Sin nombre';

  for (const [, value] of Object.entries(rawAnswers)) {
    if (typeof value === 'object' && value !== null) {
      const answer = value as Record<string, unknown>;

      // Buscar campos de nombre completo
      if (answer.first && answer.last) {
        return `${answer.first} ${answer.last}`;
      }

      // Buscar en answer.answer
      if (answer.answer && typeof answer.answer === 'object') {
        const innerAnswer = answer.answer as Record<string, unknown>;
        if (innerAnswer.first && innerAnswer.last) {
          return `${innerAnswer.first} ${innerAnswer.last}`;
        }
      }

      // Buscar campo name que contenga nombre
      const name = answer.name as string | undefined;
      if (name && typeof name === 'string') {
        const lowerName = name.toLowerCase();
        if (lowerName.includes('nombre') || lowerName.includes('name')) {
          const answerValue = answer.answer;
          if (typeof answerValue === 'string' && answerValue.trim()) {
            return answerValue;
          }
        }
      }
    }
  }

  return 'Sin nombre';
}

function ActivityItem({
  submission,
  onClick
}: {
  submission: RealtimeSubmission;
  onClick?: () => void;
}) {
  const name = extractName(submission.raw_answers);
  const hasReport = !!submission.report_url;
  const timeAgo = formatDistanceToNow(new Date(submission.created_at), {
    addSuffix: true,
    locale: es
  });

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-4 h-4 text-primary" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{name}</span>
            {submission.is_referral && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                Referido
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {submission.email && (
              <span className="flex items-center gap-1 truncate">
                <Mail className="w-3 h-3" />
                {submission.email}
              </span>
            )}
            {submission.code && (
              <span className="flex items-center gap-1">
                <span className="text-xs">#{submission.code}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="text-right">
          <Badge
            variant={hasReport ? "default" : "secondary"}
            className={hasReport ? "bg-green-100 text-green-800 hover:bg-green-100" : ""}
          >
            {hasReport ? (
              <span className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Generado
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Pendiente
              </span>
            )}
          </Badge>
          <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>
        </div>

        {onClick && (
          <ExternalLink className="w-4 h-4 text-muted-foreground" />
        )}
      </div>
    </div>
  );
}

function ActivityFeedSkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
          <Skeleton className="w-8 h-8 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
        <FileText className="w-6 h-6 text-muted-foreground" />
      </div>
      <p className="text-sm text-muted-foreground">No hay actividad reciente</p>
    </div>
  );
}

export function ActivityFeed({ submissions, isLoading, onItemClick }: ActivityFeedProps) {
  if (isLoading) {
    return <ActivityFeedSkeleton />;
  }

  if (!submissions || submissions.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-3">
      {submissions.map((submission) => (
        <ActivityItem
          key={submission.id}
          submission={submission}
          onClick={onItemClick ? () => onItemClick(submission) : undefined}
        />
      ))}
    </div>
  );
}

// Indicador de conexión Realtime
export function RealtimeIndicator({ isConnected }: { isConnected: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={`w-2 h-2 rounded-full ${
          isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
        }`}
      />
      <span className="text-xs text-muted-foreground">
        {isConnected ? 'En vivo' : 'Desconectado'}
      </span>
    </div>
  );
}
